import { useCallback } from 'react';
import { FormField, FileFieldValue } from '../types/forms';
import { useFieldValidation } from './use-field-validation.hook';
import { useValidationStore } from '../store/validation.store';
import { useApplicationStore } from '../store/application.store';
import {
  formatPhoneNumber,
  formatSSN,
  formatFederalTaxId,
  normalizeFieldValue,
} from '../utils/validation.utils';

interface UseFormFieldProps {
  field: FormField;
  onFieldChange?: (value: string | boolean | FileFieldValue | null) => void;
  disabled?: boolean;
}

interface UseFormFieldReturn {
  displayValue: string;
  handleChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSelectChange: (value: string) => Promise<void>;
  handleCheckboxChange: (checked: boolean) => void;
  handleFileChange: (files: FileFieldValue) => void;
  handleSignatureChange: (value: string) => void;
  handleBlur: () => void;
  isInvalid: boolean;
  errorMessage: string | undefined;
}

// Purpose: Custom hook to manage form field state, validation, and formatting
export const useFormField = ({
  field,
  onFieldChange,
  disabled = false,
}: UseFormFieldProps): UseFormFieldReturn => {
  const { isInvalid } = useFieldValidation(field.id);
  const { fieldErrors } = useValidationStore();
  const { editableFields } = useApplicationStore();

  // Check if user has permission to edit this field
  const canEdit = editableFields.includes(field.id);

  // Get the error message for this field
  const errorMessage = fieldErrors[field.id];

  // Purpose: Get the formatted display value for the field
  const getDisplayValue = useCallback((): string => {
    const rawValue = field.value ? String(field.value) : '';

    // Apply formatting based on field type/ID
    if (field.type === 'tax-id' || field.id === 'federalTaxId') {
      return formatFederalTaxId(rawValue);
    } else if (
      field.type === 'phone' ||
      field.id.toLowerCase().includes('phone') ||
      field.id === 'fax'
    ) {
      return formatPhoneNumber(rawValue);
    } else if (
      field.type === 'ssn' ||
      field.id.includes('SSN') ||
      field.id.includes('SocialSecurity')
    ) {
      return formatSSN(rawValue);
    }

    return rawValue;
  }, [field.value, field.id, field.type]);

  // Purpose: Normalize input value to canonical format using centralized function
  const normalizeValue = useCallback(
    (inputValue: string): string => {
      return normalizeFieldValue(field, inputValue);
    },
    [field]
  );

  // Purpose: Handle input changes for text/number/textarea fields
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (disabled || !canEdit || !onFieldChange) return;

      let inputValue = event.target.value;

      // For number fields, only allow digits (no hyphens or decimals)
      if (field.type === 'number') {
        inputValue = inputValue.replace(/\D/g, '');
      }

      // Normalize the value to canonical format
      const canonicalValue = normalizeValue(inputValue);

      // Store the canonical value in the application state
      onFieldChange(canonicalValue);
    },
    [disabled, canEdit, onFieldChange, field.id, field.type, normalizeValue]
  );

  // Purpose: Handle select dropdown changes with immediate validation
  const handleSelectChange = useCallback(
    async (value: string) => {
      if (disabled || !canEdit || !onFieldChange) return;

      onFieldChange(value);

      // Trigger immediate validation for select fields (since onBlur doesn't reliably fire)
      const validationStore = useValidationStore.getState();
      await validationStore.validateField(field, value);
    },
    [disabled, canEdit, onFieldChange, field.id, field]
  );

  // Purpose: Handle checkbox changes
  const handleCheckboxChange = useCallback(
    (checked: boolean) => {
      if (disabled || !canEdit || !onFieldChange) return;

      onFieldChange(checked);
    },
    [disabled, canEdit, onFieldChange, field.id]
  );

  // Purpose: Handle file upload changes
  const handleFileChange = useCallback(
    async (files: FileFieldValue) => {
      if (disabled || !canEdit || !onFieldChange) return;

      onFieldChange(files);

      // Trigger immediate validation for file fields
      const validationStore = useValidationStore.getState();
      await validationStore.validateField(field, files);
    },
    [disabled, canEdit, onFieldChange, field.id, field]
  );

  // Purpose: Handle signature changes with immediate validation
  const handleSignatureChange = useCallback(
    async (value: string) => {
      if (disabled || !canEdit || !onFieldChange) return;

      onFieldChange(value);

      // Trigger immediate validation for signature fields
      const validationStore = useValidationStore.getState();
      await validationStore.validateField(field, value);
    },
    [disabled, canEdit, onFieldChange, field.id, field]
  );

  // Purpose: Handle field blur events to trigger validation
  const handleBlur = useCallback(async () => {
    const validationStore = useValidationStore.getState();

    // Validate the field with its current canonical value
    await validationStore.validateField(field, field.value);
  }, [field]);

  return {
    displayValue: getDisplayValue(),
    handleChange,
    handleSelectChange,
    handleCheckboxChange,
    handleFileChange,
    handleSignatureChange,
    handleBlur,
    isInvalid,
    errorMessage,
  };
};
